'use client';

import type { ReactNode } from 'react';
import type { AdStatsResponse } from 'src/types/ads-meta/ads-meta-ops';
import type { BrandQualityFlag } from 'src/utils/brand/brand-quality-doc';
import type { MomentAnalyticsPivotResponse } from 'src/types/moments/moment-ops';
import type { ProductRankingSearchItem } from 'src/types/product-rankings/product-rankings-domain';
import type { ProductRankingSearchResponse } from 'src/types/product-rankings/product-rankings-ops';
import type { MediaCard, AccountProfile } from 'src/sections/search-all/utils/search-all-media-cards';

import { useMemo, useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';

import { fNumber } from 'src/utils/format/format-number';
import { toPivotTable } from 'src/utils/pivot/pivot-shape';
import { channelLabel, countryLabel } from 'src/utils/platform/channel-label';

import { SPACE, RADIUS } from 'src/theme/ui-tokens';
import { findMetric } from 'src/constants/pivot-fields';
import { findChartKind } from 'src/constants/pivot-charts';

import { HubChip } from 'src/components/cmui/hub/hub-chip';
import { LiveNotice } from 'src/components/cmui/live-notice';
import { HubSelect } from 'src/components/cmui/hub/hub-select';
import { HubColumns } from 'src/components/cmui/hub/hub-shell';
import { HubSection } from 'src/components/cmui/hub/hub-section';
import { HubTagCloud } from 'src/components/cmui/hub/hub-tag-cloud';
import { HubPressable } from 'src/components/cmui/hub/hub-pressable';
import { HubMediaGrid } from 'src/components/cmui/hub/hub-media-grid';
import { AnalyticsChart } from 'src/components/cmui/chart/pivot-chart';
import { HubBrandAvatar } from 'src/components/cmui/hub/hub-brand-avatar';
import { HubRankingList } from 'src/components/cmui/hub/hub-ranking-list';
import { HubAccountRow, HubAccountList } from 'src/components/cmui/hub/hub-account-row';

import { findAccountProfile } from 'src/sections/search-all/utils/search-all-media-cards';

import { BRAND_HUB_FACE_CARDS } from '../constants/brand-hub-face-size';
import { byCount, foldRows, renameRows } from '../utils/brand-hub-pivot';
import { BRAND_HUB_OVERVIEW_CARDS } from '../scope/brand-hub-overview-scope';
import { toBrandHubAdStats, toBrandHubProductStats } from '../utils/brand-hub-sell-stats';
import {
  BRAND_HUB_MIX_INFO,
  BRAND_HUB_MIX_LEGEND,
  BRAND_HUB_PIVOT_INFO,
  BRAND_HUB_ACCOUNTS_INFO,
} from '../constants/brand-hub-copy';
import {
  brandHubAdsInfo,
  brandHubPeersInfo,
  brandHubShownHint,
  brandHubAdStatsInfo,
  brandHubProductsInfo,
  brandHubProductStatsInfo,
} from '../utils/brand-hub-face-hint';

import type { BrandPeer } from '../utils/brand-hub-share';
import type { BrandHubScopeOn } from '../utils/brand-hub-scope-note';
import type { BrandHubTagRow } from '../utils/brand-hub-overview-rows';
import type { BrandHubProductRegion } from '../utils/brand-hub-product-region';

/**
 * 브랜드 허브 상세의 **「개요」 한 면** — 로그인한 상세와 공유 페이지(비회원)가 같은 부품을 쓴다.
 *
 * 🔑 두 화면은 데이터를 받는 길만 다르다(브라우저 훅 · 서버 페이지) — 구역·차례·모양·숫자는 이 한 벌이다.
 *   다른 것은 부르는 쪽이 넘기는 슬롯뿐이다: Research 띠(`researchBand`) · 실시간 보강 띠(`live`) ·
 *   AI 담기(`selection`) · 공유 페이지의 읽기 전용 격자(`publicGrid`) · 누를 때 갈 곳(`on*`).
 * ⚠️ 그림 고르개(잣대·묶기·간격·비중 잣대)는 이 부품이 쥔다 — 받은 표 안에서만 바뀐다(다시 묻지 않는다).
 *   성분·피부 고민의 표기(표준·원문)는 다른 질문이라 부르는 쪽이 쥔다.
 */

/** 개요가 읽는 데이터 — `useBrandHubData` 의 반환과 같은 이름이다 */
export type BrandHubOverviewData = {
  dailyTable: MomentAnalyticsPivotResponse | null;
  trend: MomentAnalyticsPivotResponse | null;
  channelTable: MomentAnalyticsPivotResponse | null;
  marketTable: MomentAnalyticsPivotResponse | null;
  coBrands: BrandHubTagRow[];
  hashtags: BrandHubTagRow[];
  ingredients: BrandHubTagRow[];
  concerns: BrandHubTagRow[];
  accounts: { account: string; channel: string; count: number; thumbs: string[] }[];
  adStats: AdStatsResponse | null;
  adTotal: number;
  products: ProductRankingSearchItem[];
  productTotal: number;
  productFacets: ProductRankingSearchResponse['facets'] | null;
  peers: BrandPeer[];
  /** 순위에서 뺀 브랜드 — 「같은 기간 상위 브랜드」 ⓘ 에 적는다(`brandHubPeersInfo`) */
  peersExcluded?: readonly BrandQualityFlag[];
  faces: Map<string, string>;
  railLoading: boolean;
  headLoading: boolean;
  peersLoading: boolean;
};

export type BrandHubOverviewProps = {
  name: string;
  periodLabel: string;
  data: BrandHubOverviewData;
  /** 「집행 중인 광고」·「판매몰 상품」 격자에 깔 카드(보강분이 있으면 앞에 붙인 것) */
  adCards: MediaCard[];
  productCards: MediaCard[];
  /** 「많이 다룬 계정」의 얼굴·팔로워 */
  accountProfiles: Map<string, AccountProfile>;
  rawIngredients: boolean;
  onChangeRawIngredients: (next: boolean) => void;
  rawConcerns: boolean;
  onChangeRawConcerns: (next: boolean) => void;
  /** 위 필터 줄에 걸린 조건 — 광고·상품 ⓘ 의 「필터 미적용」 줄 재료 */
  scopeOn: BrandHubScopeOn;
  /** 국가가 상품 지역 축으로 걸리나 · 해외몰만인가(`brandHubProductRegion`) */
  productRegion: BrandHubProductRegion;
  onOpenBrand: (brand: string) => void;
  onOpenCreator: (row: { channel: string; account: string }) => void;
  onOpenFace: (face: 'ads' | 'products') => void;
  /** 「많이 다룬 계정」 머리의 「크리에이터 전체」 주소 */
  creatorsHref: string;
  researchBand?: ReactNode;
  live?: { adsRunning: boolean; productsRunning: boolean; adLabel: string; productLabel: string };
  selection?: Parameters<typeof HubMediaGrid>[0]['selection'];
  publicGrid?: Parameters<typeof HubMediaGrid>[0]['publicView'];
};

/**
 * 성분·피부 고민을 **어떤 표기로** 셀지.
 * ⚠️ 표준은 못 옮긴 말을 버리고, 원문은 같은 것이 언어별로 갈라진다 — 그래서 **고를 수 있게** 둔다.
 */
const TAG_MODES = [
  { value: 'canonical', label: '표준' },
  { value: 'raw', label: '원문' },
];

/** 추이 그림의 세 토글 */
const TREND_MEASURES = [
  { value: 'count', label: '언급 수' },
  { value: 'view', label: '도달' },
];
const TREND_GROUPS = [
  { value: 'channel', label: '채널별' },
  { value: 'total', label: '전체' },
];
const TREND_GRAINS = [
  { value: 'week', label: '주별' },
  { value: 'day', label: '일별' },
];
const TREND_HINT: Record<string, string> = { channel: '채널별', total: '전체 합계' };

/** 혜택 코드값(`discount`) → 사람이 읽는 말 */

const COUNT_METRIC = findMetric('count')!;
const REACH_METRIC = findMetric('view')!;
/** 실시간 보강이 없는 자리 */
const NO_LIVE = { adsRunning: false, productsRunning: false, adLabel: '', productLabel: '' };

export function BrandHubOverview({
  name,
  periodLabel,
  data,
  adCards,
  productCards,
  accountProfiles,
  rawIngredients,
  onChangeRawIngredients,
  rawConcerns,
  onChangeRawConcerns,
  scopeOn,
  productRegion,
  onOpenBrand,
  onOpenCreator,
  onOpenFace,
  creatorsHref,
  researchBand,
  live = NO_LIVE,
  selection,
  publicGrid,
}: BrandHubOverviewProps) {
  /** 추이 그림의 세 토글 — 그림 하나로 여러 질문에 답한다 */
  const [trendMeasure, setTrendMeasure] = useState<'count' | 'view'>('count');
  /**
   * 🔑 기본은 **전체 합계 · 일별**이다 — 먼저 「얼마나 오르내렸나」가 보여야 하고, 채널을 가르거나
   *   주로 묶는 것은 그 다음 질문이다. ⛔ 기본을 채널별·주별로 바꾸지 마라(선 열 줄로 열린다).
   */
  const [trendGroup, setTrendGroup] = useState<'total' | 'channel'>('total');
  const [trendGrain, setTrendGrain] = useState<'week' | 'day'>('day');
  /** 채널 비중의 잣대 — 언급 수(기본)·도달. 추이와 **따로** 쥔다(같이 묶으면 한쪽을 못 본다) */
  const [mixMeasure, setMixMeasure] = useState<'count' | 'view'>('count');

  /**
   * 표기 고르개 — **카드마다 제 값**을 쥔다(성분과 피부 고민은 따로 움직인다).
   * 🔑 표준은 별칭·언어를 한 이름으로 모으고, 원문은 글에 적힌 말 그대로다.
   */
  const tagModePicker = (raw: boolean, setRaw: (next: boolean) => void) => (
    <HubSelect
      hideEmpty
      empty="표기"
      value={raw ? 'raw' : 'canonical'}
      options={TAG_MODES}
      onChange={(next) => setRaw(next === 'raw')}
    />
  );

  /**
   * 광고 요약 — 코드값(`discount`·`KR`)을 사람이 읽는 말로 바꾸는 것은 화면 몫이다.
   *
   * 🚨 `ads.stats` 의 `total` 은 **광고 문서 수**이고, 탭·머리에 적는 수는 `ads.list(groupBy:image)` 의
   *   **묶음(카드) 수**다 — 같은 그림을 여러 번 집행하면 문서는 늘고 카드는 하나다(실측 2,624 문서 = 1,689 카드).
   *   ⛔ **둘을 같게 만들려 하지 마라** — 뜻이 다른 수다. 이름으로 가른다(「집행 중 소재」 / 「집행 중 광고」).
   */
  const adStats = useMemo(() => toBrandHubAdStats(data.adStats), [data.adStats]);

  /**
   * 상품 요약 — 패싯은 상품 검색 응답에 실려 온다(새 조회 없음).
   * ⚠️ 평균 할인율만은 **받아 둔 표본**으로 센다(패싯에 없다) — 그 사실은 ⓘ 에 적는다(`brandHubProductStatsInfo`).
   */
  const productStats = useMemo(() => toBrandHubProductStats(data.productFacets, data.productTotal, data.products), [data.productFacets, data.productTotal, data.products]);
  /**
   * 추이 그림 하나가 세 토글을 따른다 — 잣대(언급·도달) · 묶기(전체·채널별) · 간격(주·일).
   * ⚠️ 그림 부품은 `metric` 이 아니라 **칸의 `value`** 를 읽는다 — 언급으로 볼 때는 값을 갈아 끼운다.
   */
  const trendTable = useMemo(() => {
    const raw = trendGrain === 'day' ? data.dailyTable : data.trend;
    if (!raw) return null;
    const table = trendMeasure === 'count' ? byCount(toPivotTable(raw)) : toPivotTable(raw);
    // 줄 이름은 채널 원값(`tiktok`)이다 — 범례에 그대로 새지 않게 화면 말로 바꾼다
    const named = renameRows(table, channelLabel);
    return trendGroup === 'total' ? foldRows(named) : named;
  }, [data.dailyTable, data.trend, trendGrain, trendGroup, trendMeasure]);

  /**
   * 채널 비중 — 주차 축을 접어 채널 총계만 남긴 표(도넛은 열 축을 못 그린다).
   * 🔑 **잣대를 바꿔도 다시 묻지 않는다** — 도달로 물어 온 표에 언급 수가 함께 실려 있다.
   *   언급으로 볼 때만 값을 갈아 끼운다(`byCount`) — 안 갈면 「채널 비중」이 도달 비중이 된다.
   * 🔑 모수는 머리 요약과 **같다** — 허브가 피벗을 부를 때 `beautyEval` + `basicOnly` 짝을 켜서 맞춘다.
   *   ⛔ 분석 화면의 피벗은 이것을 안 켠다 — 거기는 「수집한 것 전부」를 세는 자리다.
   */
  const channelMix = useMemo(() => {
    const table = data.channelTable ? toPivotTable(data.channelTable) : null;
    return renameRows(mixMeasure === 'count' ? byCount(table) : table, channelLabel);
  }, [data.channelTable, mixMeasure]);
  /**
   * 국가 비중 — 채널 비중과 **같은 규약**이다(도달로 물어 온 표에 언급 수가 함께 실려 온다).
   * ⚠️ 채널은 추이 집계를 접어 쓰고 국가는 제 질문을 따로 받는다 — 그 축이 추이에 없어서다.
   */
  const marketMix = useMemo(() => {
    const table = data.marketTable ? toPivotTable(data.marketTable) : null;
    return renameRows(mixMeasure === 'count' ? byCount(table) : table, countryLabel);
  }, [data.marketTable, mixMeasure]);

  return (
    <HubColumns
      stickyRail
      main={
        <>
          {/* 🔑 **처음 여는 면의 맨 위**에 둔다 — 개요가 기본 면이라 여기 없으면 대부분이 못 본다 */}
          {researchBand}
          {/*
            ⛔ 채널×주와 날짜별을 **두 그림으로 나누지 마라** — 같은 질문을 해상도만 바꾼 것이라
            고르개 하나로 충분하다(둘로 나누면 같은 답을 보려고 아래로 내려가야 한다).
            🔑 잣대를 바꿔도 **다시 묻지 않는다** — 도달로 물어 온 표에 언급 수가 함께 실려 있다(`byCount`).
          */}
          <HubSection
            title="언급 추이"
            hint={`${periodLabel} · ${TREND_HINT[trendGroup]}`}
            info={BRAND_HUB_PIVOT_INFO}
            action={
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <HubSelect hideEmpty empty="잣대" value={trendMeasure} options={TREND_MEASURES} onChange={(next) => setTrendMeasure(next === 'view' ? 'view' : 'count')} />
                <HubSelect hideEmpty empty="묶기" value={trendGroup} options={TREND_GROUPS} onChange={(next) => setTrendGroup(next === 'channel' ? 'channel' : 'total')} />
                <HubSelect hideEmpty empty="간격" value={trendGrain} options={TREND_GRAINS} onChange={(next) => setTrendGrain(next === 'day' ? 'day' : 'week')} />
              </Stack>
            }
          >
            {trendTable ? (
              <AnalyticsChart
                table={trendTable}
                metric={trendMeasure === 'view' ? REACH_METRIC : COUNT_METRIC}
                kind={findChartKind(trendGroup === 'total' ? 'area' : 'line')}
                height={260}
              />
            ) : (
              <Skeleton variant="rounded" height={260} />
            )}
          </HubSection>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
            <HubSection
              title="채널 비중"
              info={BRAND_HUB_MIX_INFO}
              action={
                <HubSelect
                  hideEmpty
                  empty="잣대"
                  value={mixMeasure}
                  options={TREND_MEASURES}
                  onChange={(next) => setMixMeasure(next === 'view' ? 'view' : 'count')}
                />
              }
            >
              {channelMix ? (
                /* 🔑 조각 색만으로는 어느 채널인지 못 읽는다 — 상위 다섯에 이름표를 단다(나머지는 「그 밖」) */
                <AnalyticsChart table={channelMix} metric={mixMeasure === 'view' ? REACH_METRIC : COUNT_METRIC} kind={findChartKind('donut')} height={240} legendTop={BRAND_HUB_MIX_LEGEND} />
              ) : (
                <Skeleton variant="rounded" height={240} />
              )}
            </HubSection>

            {/*
              🔑 **채널과 국가는 같은 그림으로 세운다**(사용자 결정) — 「어디서 도나」의 두 축이라
                한 벌로 읽혀야 한다. 잣대 고르개도 **하나**를 둘이 함께 본다(`mixMeasure`).
              ⛔ 한쪽만 다른 그림으로 바꾸지 마라 — 같은 질문의 두 축인데 결이 갈린다.
              ⚠️ 이름표는 상위 다섯까지고 나머지는 「그 밖」이다 — 국가는 꼬리가 길다.
            */}
            <HubSection title="국가 비중" info={BRAND_HUB_MIX_INFO}>
              {marketMix ? (
                <AnalyticsChart table={marketMix} metric={mixMeasure === 'view' ? REACH_METRIC : COUNT_METRIC} kind={findChartKind('donut')} height={240} legendTop={BRAND_HUB_MIX_LEGEND} />
              ) : (
                <Skeleton variant="rounded" height={240} />
              )}
            </HubSection>
          </Box>

          {/*
            **함께 나온 것 넷** — 카드 하나에 몰아 넣지 않고 **한 줄에 두 장씩**이다.
            🔑 넷은 서로 다른 축이라 길이가 제각각이다 — 한 카드에 넣으면 긴 축이 카드 높이를 정해
              짧은 축 아래가 통째로 빈다. 카드를 나누면 각자 제 높이만 쓴다.
            🔑 **짝을 지어 세운다** — 위는 「누가·무엇으로 불리나」(브랜드·해시태그), 아래는
              「무엇을 말하나」(성분·피부 고민)다. 셋을 한 줄에 세우면 알약이 너무 좁아 접힌다.
            ⚠️ 「함께 언급되는 브랜드」와 오른쪽 칸의 「상위 브랜드」는 **다른 질문**이다 —
              여기는 «이 브랜드와 같은 글에 나오는 것», 저기는 «이 브랜드와 무관한 시장 차례»다.
              한쪽을 지우거나 한 목록으로 합치지 마라.
            🔑 넷 다 **낱말 구름**이다 — 나란히 선 카드라 한 장만 순위 막대면 결이 어긋난다.
              넷 모두 「무엇이 많이 나오나」를 묻는 축이고, 값은 마우스를 올리면 나온다.
            🔑 **키를 맞춘다**(`stretch`) — 알약이 접히는 줄 수가 축마다 달라 그냥 두면
              카드 아랫단이 다 다른 높이로 끝나 한 벌로 안 읽힌다. 남는 자리는 비워 둔다.
              ⛔ 높이를 숫자로 못 박지 마라 — 좁은 화면에서 알약이 더 접히면 넘친다.
          */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'stretch' }}>
            <HubSection title="함께 언급되는 브랜드">
              <HubTagCloud rows={data.coBrands} onPick={onOpenBrand} />
            </HubSection>

            {/*
              ⚠️ **표기 고르개를 두지 않는다** — 해시태그에는 표준값이 없다(사람이 적은 말 그대로다).
                그래서 성분·고민과 달리 골라 볼 것이 없다.
            */}
            <HubSection title="함께 붙는 해시태그">
              <HubTagCloud rows={data.hashtags} />
            </HubSection>

            {/*
              ⚠️ 표기 고르개(표준·원문)는 **성분과 피부 고민 둘 다**에 걸린다(값 하나다).
                그래서 **두 카드 머리에 모두** 둔다 — 한쪽에만 두면 다른 카드가 왜 바뀌는지 알 수 없다.
            */}
            <HubSection title="함께 언급되는 성분" action={tagModePicker(rawIngredients, onChangeRawIngredients)}>
              <HubTagCloud rows={data.ingredients} />
            </HubSection>

            <HubSection title="함께 다뤄지는 피부 고민" action={tagModePicker(rawConcerns, onChangeRawConcerns)}>
              <HubTagCloud rows={data.concerns} />
            </HubSection>
          </Box>

          {/* ⚠️ 넓은 칸이라 **두 줄로** 세운다 — 한 줄로 세우면 열 줄짜리 허리가 가늘고 긴 탑이 된다 */}
          <HubSection title="많이 다룬 계정" info={BRAND_HUB_ACCOUNTS_INFO} href={creatorsHref} hrefLabel="크리에이터 전체">
            <HubAccountList compact cols={{ xs: 1, md: 2 }}>
              {data.accounts.map((row) => (
                <HubAccountRow
                  compact
                  key={`${row.channel}*${row.account}`}
                  account={row.account}
                  avatar={findAccountProfile(accountProfiles, row.channel, row.account)?.avatar}
                  followers={findAccountProfile(accountProfiles, row.channel, row.account)?.followers ?? null}
                  thumbs={row.thumbs}
                  note={`${fNumber(row.count)}건`}
                  channel={row.channel || undefined}
                  onOpen={() => onOpenCreator(row)}
                />
              ))}
              {data.accounts.length === 0 && !data.railLoading && (
                <Typography sx={{ p: 1, typography: 'uiMeta', color: 'text.secondary' }}>계정이 없습니다.</Typography>
              )}
            </HubAccountList>
          </HubSection>

          {/*
            개요는 «지금 이 브랜드가 어떤가»를 한 자리에서 보는 면이다 — 말(언급)만 있고
            **파는 쪽(광고·상품)이 없으면 반쪽**이다. 먼저 **숫자로 요약**하고, 그 아래 실물을 맛보인다.
            ⚠️ 요약은 **받아 둔 표본으로 세지 않는다** — 1,691개 중 12개의 분포는 전체의 분포가 아니다.
              광고는 서버가 전량을 센 `ads.stats`, 상품은 검색 응답에 실려 온 패싯(새 조회 없음)이다.
          */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
            <HubSection title="광고로 무엇을 말하나" info={brandHubAdStatsInfo(scopeOn)}>
              {adStats ? (
                <Stack spacing={2}>
                  <HubFactRow facts={adStats.facts} />
                  {adStats.offers.length > 0 && <HubRankingList title="내건 혜택" rows={adStats.offers} max={4} />}
                  {adStats.countries.length > 0 && <HubRankingList title="노출 국가" rows={adStats.countries} max={4} format="country" />}
                </Stack>
              ) : (
                /*
                  🚨 **0건과 「아직 안 왔다」를 가른다.** 요약은 0건이면 `null` 이라, 둘을
                    안 가르면 광고가 없는 브랜드에서 **스켈레톤이 영영 돈다**(실측).
                  🔑 실시간으로 더 찾는 중이면 그렇다고 적는다 — 「없습니다」만 뜨면
                    사용자가 기다리면 되는지를 모른다.
                */
                <BrandHubEmpty
                  loading={data.railLoading}
                  text={live.adsRunning ? live.adLabel : '이 조건에 맞는 광고가 없습니다.'}
                />
              )}
            </HubSection>

            <HubSection title="어디서 얼마에 파나" info={brandHubProductStatsInfo(scopeOn, productRegion)}>
              {productStats ? (
                <Stack spacing={2}>
                  <HubFactRow facts={productStats.facts} />
                  {productStats.malls.length > 0 && <HubRankingList title="판매몰" rows={productStats.malls} max={4} />}
                  {productStats.prices.length > 0 && <HubRankingList title="가격대" rows={productStats.prices} max={4} />}
                </Stack>
              ) : (
                <BrandHubEmpty
                  loading={data.headLoading}
                  text={live.productsRunning ? live.productLabel : '이 조건에 맞는 상품이 없습니다.'}
                />
              )}
            </HubSection>
          </Box>

          {/*
            ⛔ **0개라고 칸을 통째로 숨기지 마라** — 조건을 좁힌 사용자는 「왜 사라졌지」를
              묻게 된다. 자리는 두고 **없다고 적는다**(실시간으로 찾는 중이면 그렇다고).
          */}
          <HubSection
            title="집행 중인 광고"
            hint={brandHubShownHint(data.adTotal, Math.min(BRAND_HUB_OVERVIEW_CARDS, adCards.length))}
            info={brandHubAdsInfo(scopeOn)}
            /*
              ⛔ 「전부 보기」라고 적지 마라 — 광고 면도 상위 `BRAND_HUB_FACE_CARDS` 개까지다.
              그 면은 스스로 「N개 중 30개」라고 적고 있어, 여기서 「전부」라고 하면 한 화면이
              두 말을 한다.
            */
            action={<HubChip label={`광고 ${fNumber(BRAND_HUB_FACE_CARDS)}개 보기`} onClick={() => onOpenFace('ads')} />}
          >
            {/* 띠는 **위**에 글자 한 줄 + 진행 막대로만 선다(`compact`) — 빈 칸 자리잡기는 맛보기 칸에서 자리를 너무 먹는다 */}
            <Stack spacing={SPACE.dense} useFlexGap>
              {live.adsRunning && <LiveNotice label={live.adLabel} />}
              {adCards.length > 0 && (
                <HubMediaGrid view="thumb" perRow={BRAND_HUB_OVERVIEW_CARDS} cards={adCards.slice(0, BRAND_HUB_OVERVIEW_CARDS)} selection={selection} publicView={publicGrid} match={name} />
              )}
              {/* 🔑 띠가 이미 「찾는 중」을 말하고 있으면 여기서 또 말하지 않는다 */}
              {adCards.length === 0 && !live.adsRunning && (
                <BrandHubEmpty loading={data.headLoading} text="이 조건에 맞는 광고가 없습니다." />
              )}
            </Stack>
          </HubSection>

          <HubSection
            title="판매몰 상품"
            hint={brandHubShownHint(data.productTotal, Math.min(BRAND_HUB_OVERVIEW_CARDS, productCards.length))}
            info={brandHubProductsInfo(scopeOn, productRegion, '랭킹 높은 순')}
            action={<HubChip label={`상품 ${fNumber(BRAND_HUB_FACE_CARDS)}개 보기`} onClick={() => onOpenFace('products')} />}
          >
            <Stack spacing={SPACE.dense} useFlexGap>
              {live.productsRunning && <LiveNotice label={live.productLabel} />}
              {productCards.length > 0 && (
                <HubMediaGrid view="thumb" perRow={BRAND_HUB_OVERVIEW_CARDS} cards={productCards.slice(0, BRAND_HUB_OVERVIEW_CARDS)} selection={selection} publicView={publicGrid} match={name} />
              )}
              {productCards.length === 0 && !live.productsRunning && (
                <BrandHubEmpty loading={data.headLoading} text="이 조건에 맞는 상품이 없습니다." />
              )}
            </Stack>
          </HubSection>
        </>
      }
      rail={
        /*
          오른쪽 칸은 **이 브랜드 밖**이다 — 여기서 보는 것은 «남들은 어떤가»이고,
          이 브랜드에 딸린 것(추이·비중·함께 언급·계정·광고·상품)은 전부 왼쪽 본문에 선다.
          ⛔ 이 브랜드의 숫자를 여기에 들이지 마라 — 왼쪽과 같은 것을 두 번 읽게 된다.
        */
        <HubSection dense title="같은 기간 상위 브랜드" info={brandHubPeersInfo(data.peersExcluded ?? NO_EXCLUDED)}>
          {data.peers.length > 0 ? (
            <PeerBars rows={data.peers} faces={data.faces} onPick={onOpenBrand} />
          ) : (
            /* 같은 병 — 0명이면 스켈레톤이 영영 돈다 */
            <BrandHubEmpty loading={data.peersLoading} text="이 조건에 맞는 브랜드가 없습니다." />
          )}
        </HubSection>
      }
    />
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

const NO_EXCLUDED: readonly BrandQualityFlag[] = [];

/** 요약 숫자 몇 개를 한 줄로 — 개요의 광고·상품 칸이 쓴다 */
function HubFactRow({ facts }: { facts: { label: string; value: string }[] }) {
  return (
    <Stack direction="row" spacing={1} divider={<Box sx={{ width: '1px', bgcolor: 'divider' }} />}>
      {facts.map((fact) => (
        <Box key={fact.label} sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ typography: 'uiMeta', color: 'text.disabled' }}>{fact.label}</Typography>
          <Typography noWrap sx={{ typography: 'uiHead', letterSpacing: '-.02em' }}>{fact.value}</Typography>
        </Box>
      ))}
    </Stack>
  );
}

/** 비었거나 오는 중 — 0건과 「아직 안 왔다」를 가른다(안 가르면 스켈레톤이 영영 돈다) */
export function BrandHubEmpty({ loading, text }: { loading: boolean; text: string }) {
  if (loading) return <Skeleton variant="rounded" height={140} />;
  return <Typography sx={{ py: 3, textAlign: 'center', typography: 'uiBody', color: 'text.secondary' }}>{text}</Typography>;
}

/**
 * 상위 브랜드 막대 — **이 브랜드 줄만 진하게** 칠해 「어디쯤인가」를 한눈에 보인다.
 * 차트 부품을 안 쓴다 — 줄을 눌러 그 브랜드로 건너뛰어야 하고, 자기 줄만 다른 색이어야 한다.
 */
function PeerBars({ rows, faces, onPick }: { rows: BrandPeer[]; faces: Map<string, string>; onPick: (brand: string) => void }) {
  const max = Math.max(1, ...rows.map((row) => row.mentions));
  return (
    /* 🔑 **스무 줄이 쌓이는 자리**라 카드를 벗는다 — 테두리가 스무 겹이면 상자가 내용보다 먼저 보인다 */
    <Stack spacing={0}>
      {rows.map((row) => (
        /* 자기 줄은 갈 곳이 없다 — 모양은 같고 손짓만 뺀다 */
        <HubPressable key={row.brand} dense flat disabled={row.self} onPress={() => onPick(row.brand)}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography sx={{ typography: 'uiMeta', color: 'text.disabled', width: 16, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{row.rank}</Typography>
            {/* 얼굴은 공식 계정에서 빌려 온 추정값이다 — 없으면 머리글자로 떨어진다 */}
            <HubBrandAvatar src={faces.get(row.brand.trim().toLowerCase())} name={row.brand} size={20} />
            <Typography noWrap sx={{ flex: 1, minWidth: 0, typography: 'uiBody', fontWeight: row.self ? 800 : 600, color: row.self ? 'primary.main' : 'text.primary' }}>
              {row.brand}
            </Typography>
            <Typography sx={{ typography: 'uiAction', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>{fNumber(row.mentions)}</Typography>
          </Stack>
          <Box sx={{ mt: 0.5, height: 4, borderRadius: RADIUS.pill, bgcolor: 'grey.200', overflow: 'hidden' }}>
            <Box sx={{ width: `${(row.mentions / max) * 100}%`, height: 1, borderRadius: RADIUS.pill, bgcolor: row.self ? 'primary.main' : 'primary.light' }} />
          </Box>
        </HubPressable>
      ))}
    </Stack>
  );
}
